import { Breadcrumb, BreadcrumbItem, Modal } from "flowbite-react";
import { HiHome, HiFilter } from "react-icons/hi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "@repo/core";

// Components
import FactorFilters from "./filters/FactorFilters";
import IndicatorsTable, {
  type SortConfig,
  type SortField,
} from "./table/IndicatorsTable"; // Now works with Strategy data
import PerformanceChart from "./chart/PerformanceChart";
import { UniverseBadges } from "./UniverseBadges";
import { QuickFilterBadges } from "./QuickFilterBadges";

// Hooks and types
import type { FilterCriteria, QuickFilter } from "../../types/factor-mimicking";
import {
  useMimickingChartData,
  useMimickingData,
  useUniverseChartData,
} from "@repo/api";
import {
  useIndicatorDetails,
  useQuickFilters,
  useUniverseFilterData,
} from "../../hooks/api/useUniversesApi";

// Hardcoded valid universes - no backend dependency to avoid re-renders
const DEFAULT_UNIVERSE = "sp500";

// Quick filter definitions
// const QUICK_FILTERS: QuickFilter[] = [
//   {
//     id: "best-sharpe-3y",
//     name: "Best Sharpe 3Y",
//     filters: {
//       universe: "sp500",
//       sharpeRatio: { operator: "gt", value: 1.6 },
//       period: "3Y",
//       sortBy: "Sharpe",
//       sortOrder: "desc",
//     },
//   },
//   {
//     id: "best-cagr-ytd",
//     name: "Best CAGR YTD",
//     filters: {
//       universe: "sp500",
//       cagr: { operator: "gt", value: 66.6 },
//       period: "YTD",
//       sortBy: "CAGR",
//       sortOrder: "desc",
//     },
//   },
//   {
//     id: "smallest-turnover-5y",
//     name: "Smallest Turnover 5Y",
//     filters: {
//       universe: "sp500",
//       meanTurnover: { operator: "lt", value: 3.0 },
//       period: "5Y",
//       sortBy: "meanTurnover",
//       sortOrder: "asc",
//     },
//   },
//   {
//     id: "smallest-volatility-all",
//     name: "Smallest Volatility All",
//     filters: {
//       universe: "sp500",
//       volatility: { operator: "lt", value: 17.0 },
//       period: "ALL",
//       sortBy: "Volatility",
//       sortOrder: "asc",
//     },
//   },
// ];

// Helper function to convert FilterCriteria sortBy to SortField
const mapSortByToSortField = (sortBy?: string): SortField => {
  switch (sortBy) {
    case "Sharpe":
      return "Sharpe";
    case "CAGR":
      return "CAGR";
    case "Volatility":
      return "Volatility";
    case "meanTurnover":
      return "meanTurnover";
    default:
      return "CAGR";
  }
};

// Helper function to convert SortField to FilterCriteria sortBy format
const mapSortFieldToSortBy = (sortField: SortField): string => {
  switch (sortField) {
    case "Sharpe":
      return "Sharpe";
    case "CAGR":
      return "CAGR";
    case "Volatility":
      return "Volatility";
    case "meanTurnover":
      return "meanTurnover";
    case "name":
      return "name";
    default:
      return "CAGR";
  }
};

const FactorMimicking = () => {
  const navigate = useNavigate();
  const { t, tCommon, tTable } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const { data: universeFilterData } = useUniverseFilterData();
  const VALID_UNIVERSES = useMemo(
    () => universeFilterData?.map((universe) => universe.modelsKey) || [],
    [universeFilterData]
  );
  const { data: indicatorDetails } = useIndicatorDetails();

  // Universe tab state - initialized from URL
  const initialUniverse = useMemo(() => {
    const universeParam = searchParams.get("universe");
    return universeParam && VALID_UNIVERSES.includes(universeParam)
      ? universeParam
      : DEFAULT_UNIVERSE;
  }, [searchParams, VALID_UNIVERSES]);

  const [selectedUniverse, setSelectedUniverse] = useState(initialUniverse);

  // Helper function to parse filters from URL
  const quickFiltersQuery = useQuickFilters();
  const QUICK_FILTERS = useMemo<QuickFilter[]>(() => {
    return quickFiltersQuery.data || [];
  }, [quickFiltersQuery.data]);

  const getFiltersFromUrl = useMemo((): FilterCriteria => {
    const universeParam = searchParams.get("universe");

    const universe =
      universeParam && VALID_UNIVERSES.includes(universeParam)
        ? universeParam
        : DEFAULT_UNIVERSE;

    const indicator = searchParams.get("indicator") || undefined;
    const styleParam = searchParams.get("style");
    const style = styleParam
      ? styleParam.split(",").filter(Boolean)
      : undefined;

    // Parse Sharpe ratio from URL
    const sharpeOperator = searchParams.get("sharpeOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const sharpeValue = parseFloat(searchParams.get("sharpeValue") || "");
    const sharpeRatio =
      sharpeOperator && !isNaN(sharpeValue)
        ? { operator: sharpeOperator, value: sharpeValue }
        : undefined;

    // Parse Volatility from URL (percentage values 0-100)
    const volatilityOperator = searchParams.get("volatilityOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const volatilityValue = parseFloat(
      searchParams.get("volatilityValue") || ""
    );
    const volatility =
      volatilityOperator && !isNaN(volatilityValue)
        ? { operator: volatilityOperator, value: volatilityValue }
        : undefined;

    // Parse CAGR from URL (percentage values 0-100)
    const cagrOperator = searchParams.get("cagrOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const cagrValue = parseFloat(searchParams.get("cagrValue") || "");
    const cagr =
      cagrOperator && !isNaN(cagrValue)
        ? { operator: cagrOperator, value: cagrValue }
        : undefined;

    // Parse Mean Turnover from URL (percentage values 0-200)
    const turnoverOperator = searchParams.get("turnoverOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const turnoverValue = parseFloat(searchParams.get("turnoverValue") || "");
    const meanTurnover =
      turnoverOperator && !isNaN(turnoverValue)
        ? { operator: turnoverOperator, value: turnoverValue }
        : undefined;

    return {
      universe,
      indicator,
      style,
      sharpeRatio,
      volatility,
      cagr,
      meanTurnover,
    };
  }, [searchParams, VALID_UNIVERSES]);

  // Get initial values from URL parameters
  const initialPeriod = useMemo(() => {
    const period = searchParams.get("period") || "3Y";
    // Validate period value
    const validPeriods = ["ALL", "5Y", "3Y", "1Y", "YTD"];
    return validPeriods.includes(period) ? period : "3Y";
  }, [searchParams]);

  const initialPage = useMemo(() => {
    const page = parseInt(searchParams.get("page") || "1");
    return page > 0 ? page : 1;
  }, [searchParams]);

  const initialLimit = useMemo(() => {
    const limit = parseInt(searchParams.get("limit") || "10");
    return [10, 20, 50].includes(limit) ? limit : 10;
  }, [searchParams]);

  const initialSortConfig = useMemo((): SortConfig => {
    const sortBy = searchParams.get("sortBy") || "CAGR";
    const sortOrder = searchParams.get("sortOrder") || "desc";

    // Validate sort field - add more valid fields as needed
    const validSortFields: SortField[] = [
      "CAGR",
      "Sharpe",
      "Volatility",
      "meanTurnover",
      "name",
    ];
    const validSortDirections = ["asc", "desc"];

    return {
      field: validSortFields.includes(sortBy as SortField)
        ? (sortBy as SortField)
        : "CAGR",
      direction: validSortDirections.includes(sortOrder)
        ? (sortOrder as "asc" | "desc")
        : "desc",
    };
  }, [searchParams]);

  // Filter state - initialized from URL
  const [filters, setFilters] = useState<FilterCriteria>(getFiltersFromUrl);

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);
  const [sortConfig, setSortConfig] = useState<SortConfig>(initialSortConfig);

  // Selection state for chart filtering
  const [selectedStrategyIds, setSelectedStrategyIds] = useState<Set<string>>(new Set());

  // Quick filter state
  const [selectedQuickFilterId, setSelectedQuickFilterId] = useState<
    string | null
  >(null);


  // Function to update URL parameters - memoized to prevent re-renders
  const updateUrlParams = useCallback(
    (newParams: Record<string, string | undefined>) => {
      const updatedParams = new URLSearchParams(searchParams);

      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          updatedParams.set(key, value);
        } else {
          updatedParams.delete(key);
        }
      });

      setSearchParams(updatedParams);
    },
    [searchParams, setSearchParams]
  );

  // Sync state with URL parameters when they change - only depend on searchParams
  useEffect(() => {
    // Parse filters directly in effect to avoid dependency issues
    const universeParam = searchParams.get("universe");
    // Use universe param directly, let data validation happen elsewhere
    const universe = universeParam || DEFAULT_UNIVERSE;

    const indicator = searchParams.get("indicator") || undefined;
    const styleParam = searchParams.get("style");
    const style = styleParam
      ? styleParam.split(",").filter(Boolean)
      : undefined;

    // Parse Sharpe ratio from URL
    const sharpeOperator = searchParams.get("sharpeOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const sharpeValue = parseFloat(searchParams.get("sharpeValue") || "");
    const sharpeRatio =
      sharpeOperator && !isNaN(sharpeValue)
        ? { operator: sharpeOperator, value: sharpeValue }
        : undefined;

    // Parse Volatility from URL (percentage values 0-100)
    const volatilityOperator = searchParams.get("volatilityOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const volatilityValue = parseFloat(
      searchParams.get("volatilityValue") || ""
    );
    const volatility =
      volatilityOperator && !isNaN(volatilityValue)
        ? { operator: volatilityOperator, value: volatilityValue }
        : undefined;

    // Parse CAGR from URL (percentage values 0-100)
    const cagrOperator = searchParams.get("cagrOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const cagrValue = parseFloat(searchParams.get("cagrValue") || "");
    const cagr =
      cagrOperator && !isNaN(cagrValue)
        ? { operator: cagrOperator, value: cagrValue }
        : undefined;

    // Parse Mean Turnover from URL (percentage values 0-200)
    const turnoverOperator = searchParams.get("turnoverOperator") as
      | "lt"
      | "eq"
      | "gt"
      | null;
    const turnoverValue = parseFloat(searchParams.get("turnoverValue") || "");
    const meanTurnover =
      turnoverOperator && !isNaN(turnoverValue)
        ? { operator: turnoverOperator, value: turnoverValue }
        : undefined;

    const urlFilters: FilterCriteria = {
      universe,
      indicator,
      style,
      sharpeRatio,
      volatility,
      cagr,
      meanTurnover,
    };

    // Parse pagination and sort parameters directly to avoid circular deps
    const periodParam = searchParams.get("period") || "3Y";
    // Validate period value
    const validPeriods = ["ALL", "5Y", "3Y", "1Y", "YTD"];
    const period = validPeriods.includes(periodParam) ? periodParam : "3Y";

    const pageParam = searchParams.get("page");
    const page = pageParam ? parseInt(pageParam, 10) : 1;

    const limitParam = searchParams.get("limit");
    const limit = limitParam ? parseInt(limitParam, 10) : 10;

    const sortBy = searchParams.get("sortBy") || "Sharpe";
    const sortOrder = (searchParams.get("sortOrder") || "desc") as
      | "asc"
      | "desc";

    // Create extended filter object with period and sort for matching
    const extendedUrlFilters = {
      ...urlFilters,
      period,
      sortBy: mapSortFieldToSortBy(sortBy as SortField),
      sortOrder,
    };

    // Check if current filters match any quick filter
    const matchingQuickFilter = QUICK_FILTERS.find((qf) => {
      const qfFilters = qf.filters;

      // Compare each property explicitly to avoid JSON stringification issues
      if (qfFilters.universe !== extendedUrlFilters.universe) return false;
      if (qfFilters.period !== extendedUrlFilters.period) return false;
      if (qfFilters.sortBy !== extendedUrlFilters.sortBy) return false;
      if (qfFilters.sortOrder !== extendedUrlFilters.sortOrder) return false;

      // Compare indicator (should be undefined in quick filters)
      if (qfFilters.indicator !== extendedUrlFilters.indicator) return false;

      // Compare style (should be undefined in quick filters)
      if (qfFilters.style !== extendedUrlFilters.style) return false;

      // Compare sharpeRatio objects
      if (qfFilters.sharpeRatio && extendedUrlFilters.sharpeRatio) {
        if (
          qfFilters.sharpeRatio.operator !==
          extendedUrlFilters.sharpeRatio.operator
        )
          return false;
        if (
          qfFilters.sharpeRatio.value !== extendedUrlFilters.sharpeRatio.value
        )
          return false;
      } else if (qfFilters.sharpeRatio !== extendedUrlFilters.sharpeRatio) {
        return false;
      }

      // Compare volatility objects
      if (qfFilters.volatility && extendedUrlFilters.volatility) {
        if (
          qfFilters.volatility.operator !==
          extendedUrlFilters.volatility.operator
        )
          return false;
        if (qfFilters.volatility.value !== extendedUrlFilters.volatility.value)
          return false;
      } else if (qfFilters.volatility !== extendedUrlFilters.volatility) {
        return false;
      }

      // Compare cagr objects
      if (qfFilters.cagr && extendedUrlFilters.cagr) {
        if (qfFilters.cagr.operator !== extendedUrlFilters.cagr.operator)
          return false;
        if (qfFilters.cagr.value !== extendedUrlFilters.cagr.value)
          return false;
      } else if (qfFilters.cagr !== extendedUrlFilters.cagr) {
        return false;
      }

      // Compare meanTurnover objects
      if (qfFilters.meanTurnover && extendedUrlFilters.meanTurnover) {
        if (
          qfFilters.meanTurnover.operator !==
          extendedUrlFilters.meanTurnover.operator
        )
          return false;
        if (
          qfFilters.meanTurnover.value !== extendedUrlFilters.meanTurnover.value
        )
          return false;
      } else if (qfFilters.meanTurnover !== extendedUrlFilters.meanTurnover) {
        return false;
      }

      return true;
    });

    setFilters(urlFilters);
    setFilters(urlFilters);
    setSelectedUniverse(universe); // Sync selectedUniverse with URL parameter
    setSelectedPeriod(period);
    setCurrentPage(page > 0 ? page : 1);
    setItemsPerPage(limit);
    setSortConfig({ field: sortBy as SortField, direction: sortOrder });

    // Only update selectedQuickFilterId if it actually changed to prevent unnecessary re-renders
    const newQuickFilterId = matchingQuickFilter?.SK || null;
    setSelectedQuickFilterId((prev) =>
      prev !== newQuickFilterId ? newQuickFilterId : prev
    );
  }, [searchParams, QUICK_FILTERS]);

  // Data hooks

  // Get strategies data from backend - use selectedUniverse for data fetching
  const { data: strategiesData, isLoading: strategiesLoading } =
    useMimickingData(selectedUniverse || "", selectedPeriod);

  // Filter and paginate strategies based on current filters
  const { strategies, totalCount, totalPages } = useMemo(() => {
    if (!strategiesData) {
      return { strategies: [], totalCount: 0, totalPages: 0 };
    }

    let filteredStrategies = [...strategiesData];

    // Apply filters - use filters for actual filtering
    if (filters.indicator) {
      const searchTerm = filters.indicator.toLowerCase();
      filteredStrategies = filteredStrategies.filter((strategy) => {
        const isInName = strategy.name.toLowerCase().includes(searchTerm)
        const factorName = Object.keys(strategy.factor || {})[0];
        const isInDescription = indicatorDetails?.[factorName]?.description
          .toLowerCase()
          .includes(searchTerm);
        return isInName || isInDescription;

      });
    }

    if (filters.style && filters.style.length > 0) {
      // Check if strategy.style matches any of the selected style filters
      filteredStrategies = filteredStrategies.filter((strategy) =>
        filters.style!.includes(strategy.style)
      );
    }

    if (filters.sharpeRatio) {
      const { operator, value } = filters.sharpeRatio;
      filteredStrategies = filteredStrategies.filter((strategy) => {
        switch (operator) {
          case "lt":
            return strategy.Sharpe < value;
          case "eq":
            return Math.abs(strategy.Sharpe - value) < 0.01;
          case "gt":
            return strategy.Sharpe > value;
          default:
            return true;
        }
      });
    }

    if (filters.volatility) {
      const { operator, value } = filters.volatility;
      // Convert percentage input (0-100) to decimal (0-1) for comparison
      const decimalValue = value / 100;
      filteredStrategies = filteredStrategies.filter((strategy) => {
        switch (operator) {
          case "lt":
            return strategy.Volatility < decimalValue;
          case "eq":
            return Math.abs(strategy.Volatility - decimalValue) < 0.0001;
          case "gt":
            return strategy.Volatility > decimalValue;
          default:
            return true;
        }
      });
    }

    if (filters.cagr) {
      const { operator, value } = filters.cagr;
      // Convert percentage input (0-100) to decimal (0-1) for comparison
      const decimalValue = value / 100;
      filteredStrategies = filteredStrategies.filter((strategy) => {
        switch (operator) {
          case "lt":
            return strategy.CAGR < decimalValue;
          case "eq":
            return Math.abs(strategy.CAGR - decimalValue) < 0.0001;
          case "gt":
            return strategy.CAGR > decimalValue;
          default:
            return true;
        }
      });
    }

    if (filters.meanTurnover) {
      const { operator, value } = filters.meanTurnover;
      // Convert percentage input (0-100) to decimal (0-1) for comparison
      const decimalValue = value / 100;
      filteredStrategies = filteredStrategies.filter((strategy) => {
        switch (operator) {
          case "lt":
            return strategy.meanTurnover < decimalValue;
          case "eq":
            return Math.abs(strategy.meanTurnover - decimalValue) < 0.0001;
          case "gt":
            return strategy.meanTurnover > decimalValue;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filteredStrategies.sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      // Handle string sorting (for "name") vs number sorting (for metrics)
      if (typeof aValue === "string" && typeof bValue === "string") {
        if (sortConfig.direction === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else {
        // Handle numeric sorting
        const numA = typeof aValue === "number" ? aValue : 0;
        const numB = typeof bValue === "number" ? bValue : 0;
        if (sortConfig.direction === "asc") {
          return numA - numB;
        } else {
          return numB - numA;
        }
      }
    });

    // Calculate pagination
    const totalCount = filteredStrategies.length;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedStrategies = filteredStrategies.slice(startIndex, endIndex);

    return {
      strategies: paginatedStrategies,
      totalCount,
      totalPages,
    };
  }, [strategiesData, filters, currentPage, itemsPerPage, sortConfig]);

  // No need to transform strategies - table will consume them directly

  // Get timelines for performance chart using selected strategy names
  // Use selectedStrategyIds directly to include strategies from all pages
  const selectedStrategyNames = useMemo(() => {
    if (selectedStrategyIds.size === 0) {
      // If nothing is selected, return empty array to avoid fetching data
      return [];
    }
    // Convert Set to Array - this includes strategies from all pages
    return Array.from(selectedStrategyIds);
  }, [selectedStrategyIds]);

  const { data: chartData, isLoading: chartLoading } = useMimickingChartData(
    selectedUniverse || "",
    selectedPeriod,
    selectedStrategyNames
  );

  // Loaded as soon as a universe is selected, independent of indicator
  // selection - same loading pattern as the per-indicator chart data above.
  const universeKey = selectedUniverse ? `admin_${selectedUniverse}_M` : "";
  const { data: universeChartData, isLoading: universeChartLoading } =
    useUniverseChartData(universeKey);

  // Label the universe line with the same display name shown in the Universe
  // selection badges (e.g. "S&P 500"), falling back to the generic label.
  const selectedUniverseName = useMemo(() => {
    const universe = universeFilterData?.find(
      (item) => item.modelsKey === selectedUniverse
    );
    return universe?.universe || tTable("universe");
  }, [universeFilterData, selectedUniverse, tTable]);

  const timelines = useMemo(() => {
    const result: {
      indicatorId: string;
      indicatorName: string;
      data: { date: string; value: number }[];
    }[] = [];

    const universePerformanceData =
      universeChartData?.[universeKey]?.[selectedPeriod];
    if (universePerformanceData) {
      result.push({
        indicatorId: universeKey,
        indicatorName: selectedUniverseName,
        data: Object.entries(universePerformanceData)
          .map(([date, value]) => ({ date, value }))
          .sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          ),
      });
    }

    if (!strategiesData || !chartData || selectedStrategyIds.size === 0) {
      return result;
    }

    // Get selected strategies from the full dataset (not just current page)
    const strategiesToShow = strategiesData.filter((strategy) =>
      selectedStrategyIds.has(strategy.modelId)
    );

    // Transform chartData (which uses modelId as keys) into timeline format
    strategiesToShow.forEach((strategy) => {
      const performanceData = chartData[strategy.modelId];

      let timelineData: { date: string; value: number }[] = [];

      if (performanceData) {
        // Convert PerformanceMetrics (date->value object) to TimelineDataPoint array
        timelineData = Object.entries(performanceData)
          .map(([date, value]) => ({
            date,
            value,
          }))
          .sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          );
      }

      result.push({
        indicatorId: strategy.modelId,
        indicatorName: strategy.name,
        data: timelineData,
      });
    });

    return result;
  }, [
    strategiesData,
    chartData,
    selectedStrategyIds,
    universeChartData,
    universeKey,
    selectedPeriod,
    selectedUniverseName,
  ]);

  // Event handlers - memoized to prevent re-renders
  const handleFiltersChange = useCallback((newFilters: FilterCriteria) => {
    // Apply filters immediately
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change

    // Update URL parameters (preserve sort settings)
    updateUrlParams({
      universe: newFilters.universe,
      indicator: newFilters.indicator,
      style: newFilters.style?.join(","),
      sharpeOperator: newFilters.sharpeRatio?.operator,
      sharpeValue: newFilters.sharpeRatio?.value?.toString(),
      volatilityOperator: newFilters.volatility?.operator,
      volatilityValue: newFilters.volatility?.value?.toString(),
      cagrOperator: newFilters.cagr?.operator,
      cagrValue: newFilters.cagr?.value?.toString(),
      turnoverOperator: newFilters.meanTurnover?.operator,
      turnoverValue: newFilters.meanTurnover?.value?.toString(),
      page: "1", // Reset page
      sortBy: sortConfig.field, // Preserve sort field
      sortOrder: sortConfig.direction, // Preserve sort direction
    });
  }, [sortConfig, updateUrlParams]);

  const handleQuickFilterSelect = useCallback(
    (quickFilter: QuickFilter) => {
      // Check if this quick filter is already selected
      const isCurrentlySelected = selectedQuickFilterId === quickFilter.SK;
      if (isCurrentlySelected) {
        // Deselect - reset to default filters
        setSelectedQuickFilterId(null);
        const resetFilters = {
          universe: DEFAULT_UNIVERSE,
        };
        setFilters({ ...resetFilters });
        setSelectedUniverse(DEFAULT_UNIVERSE); // Update universe state
      } else {
        // Apply the quick filter - create new objects to ensure React detects changes
        setSelectedQuickFilterId(quickFilter.SK);
        const newFilters = { ...quickFilter.filters };
        setFilters(newFilters);

        // Update selected universe to match the quick filter
        if (quickFilter.filters.universe) {
          setSelectedUniverse(quickFilter.filters.universe);
        }

        // Apply period if specified in quick filter
        if (quickFilter.filters.period) {
          setSelectedPeriod(quickFilter.filters.period);
        }

        // Apply sort config if specified in quick filter
        if (quickFilter.filters.sortBy) {
          setSortConfig({
            field: mapSortByToSortField(quickFilter.filters.sortBy),
            direction: quickFilter.filters.sortOrder || "desc",
          });
        }
      }

      // Reset pagination when applying quick filter
      setCurrentPage(1);

      // Update URL parameters
      const filtersToApply = isCurrentlySelected
        ? { universe: DEFAULT_UNIVERSE }
        : quickFilter.filters;

      updateUrlParams({
        universe: filtersToApply.universe,
        indicator: filtersToApply.indicator,
        style: filtersToApply.style?.join(","),
        sharpeOperator: filtersToApply.sharpeRatio?.operator,
        sharpeValue: filtersToApply.sharpeRatio?.value?.toString(),
        volatilityOperator: filtersToApply.volatility?.operator,
        volatilityValue: filtersToApply.volatility?.value?.toString(),
        cagrOperator: filtersToApply.cagr?.operator,
        cagrValue: filtersToApply.cagr?.value?.toString(),
        turnoverOperator: filtersToApply.meanTurnover?.operator,
        turnoverValue: filtersToApply.meanTurnover?.value?.toString(),
        period: filtersToApply.period || undefined,
        page: "1",
        sortBy: filtersToApply.sortBy || sortConfig.field,
        sortOrder: filtersToApply.sortOrder || sortConfig.direction,
      });
    },
    [selectedQuickFilterId, sortConfig, updateUrlParams]
  );

  const handleFiltersReset = useCallback(() => {
    const resetFilters = {
      universe: "sp500", // Always keep S&P 500 selected even on reset
    };

    // Reset both applied and pending filters
    setFilters(resetFilters);
    setFilters(resetFilters);
    setCurrentPage(1);
    // Clear selected quick filter
    setSelectedQuickFilterId(null);
    // Reset period to default
    setSelectedPeriod("3Y");

    // Update URL parameters (preserve sort settings)
    updateUrlParams({
      universe: resetFilters.universe,
      indicator: undefined,
      style: undefined,
      sharpeOperator: undefined,
      sharpeValue: undefined,
      volatilityOperator: undefined,
      volatilityValue: undefined,
      cagrOperator: undefined,
      cagrValue: undefined,
      turnoverOperator: undefined,
      turnoverValue: undefined,
      period: "3Y", // Reset to default period
      page: "1",
      sortBy: sortConfig.field, // Preserve sort field
      sortOrder: sortConfig.direction, // Preserve sort direction
    });
  }, [sortConfig, updateUrlParams]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);

      // Update URL parameters
      updateUrlParams({
        page: page.toString(),
        sortBy: sortConfig.field, // Preserve sort field
        sortOrder: sortConfig.direction, // Preserve sort direction
      });
    },
    [sortConfig, updateUrlParams]
  );

  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage: number) => {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);

      // Update URL parameters (preserve sort settings)
      updateUrlParams({
        limit: newItemsPerPage.toString(),
        page: "1",
        sortBy: sortConfig.field, // Preserve sort field
        sortOrder: sortConfig.direction, // Preserve sort direction
      });
    },
    [sortConfig, updateUrlParams]
  );

  const handlePeriodChange = useCallback(
    (period: string) => {
      setSelectedPeriod(period);

      // Update URL parameters (preserve sort settings)
      updateUrlParams({
        period: period,
        page: "1", // Reset page when period changes
        sortBy: sortConfig.field, // Preserve sort field
        sortOrder: sortConfig.direction, // Preserve sort direction
      });
      setCurrentPage(1);
    },
    [sortConfig, updateUrlParams]
  );

  const handleSortChange = useCallback(
    (newSortConfig: SortConfig) => {
      setSortConfig(newSortConfig);
      setCurrentPage(1); // Reset to first page when sorting changes

      // Update URL parameters
      updateUrlParams({
        sortBy: newSortConfig.field,
        sortOrder: newSortConfig.direction,
        page: "1", // Reset page when sorting changes
      });
    },
    [updateUrlParams]
  );

  // Selection handlers
  const handleSelectionChange = useCallback((strategyId: string, selected: boolean) => {
    setSelectedStrategyIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(strategyId);
      } else {
        newSet.delete(strategyId);
      }
      return newSet;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedStrategyIds(new Set());
  }, []);

  return (
    <div className="p-6 mx-auto">
      {/* Breadcrumb */}
      <div className="mb-4">
        <Breadcrumb>
          <BreadcrumbItem
            href="/"
            icon={HiHome}
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            {tCommon("breadcrumb.overview")}
          </BreadcrumbItem>
          <BreadcrumbItem>
            {tCommon("breadcrumb.chooseIndicator")}
          </BreadcrumbItem>
        </Breadcrumb>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("factorMimicking.title", "Choose Indicator")}
        </h1>
        <p className="text-gray-600 mt-1">
          {t(
            "factorMimicking.subtitle",
            "The strategy is based on your chosen indicator, with parameters you can adjust to your preferences."
          )}
        </p>

        {/* Filter Indicators Button - visible only on small screens */}
        <div className="mt-3 lg:hidden">
          <button
            className="w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 transition-all duration-200 border hover:shadow-md hover:scale-105 flex items-center justify-center gap-2 cursor-pointer"
            style={{
              backgroundColor: "#EBF5FF",
              borderColor: "#A4CAFE",
              color: "#1A55DB",
            }}
            onClick={() => setIsFilterModalOpen(true)}
            data-tour="filter-indicators-button"
          >
            <HiFilter size={16} />
            <span>
              {t("factorMimicking.filterIndicators", "Filter Indicators")}
            </span>
          </button>
        </div>
      </div>

      {/* Main Content - Single Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Quick Filters - Badge Style */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
            {t("factorMimicking.namedFilters", "Named Filters")}
          </h3>
          <QuickFilterBadges
            quickFilters={QUICK_FILTERS}
            selectedQuickFilterId={selectedQuickFilterId}
            onQuickFilterSelect={handleQuickFilterSelect}
            isLoading={quickFiltersQuery.isLoading}
            initialVisibleCount={5}
          />
        </div>

        {/* Universe Selection - Badge Style */}
        <div className="px-6 pt-4 pb-4 border-b border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
            {t("factorMimicking.universeSelection", "Universe Selection")}
          </h3>
          <UniverseBadges
            universes={universeFilterData || []}
            selectedUniverse={selectedUniverse}
            onUniverseSelect={(universeKey) => {
              setSelectedUniverse(universeKey);
              // Update URL
              updateUrlParams({ universe: universeKey });
              // Reset filters when universe changes
              const resetFilters = { universe: universeKey };
              setFilters(resetFilters);
              setFilters(resetFilters);
              setCurrentPage(1);
            }}
            initialVisibleCount={5}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* Left Side - Table and Chart (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Strategies Table */}
            <div>
              <IndicatorsTable
                strategies={strategies}
                isLoading={strategiesLoading}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                selectedPeriod={selectedPeriod}
                selectedUniverse={selectedUniverse}
                sortConfig={sortConfig}
                onSortChange={handleSortChange}
                indicatorDetails={indicatorDetails}
                selectedStrategyIds={selectedStrategyIds}
                onSelectionChange={handleSelectionChange}
                onClearSelection={handleClearSelection}
                onReset={handleFiltersReset}
              />
            </div>

            {/* Performance Chart */}
            <div className="border-t border-gray-200 pt-6">
              <PerformanceChart
                timelines={timelines}
                isLoading={
                  strategiesLoading || chartLoading || universeChartLoading
                }
                hasSelection={selectedStrategyIds.size > 0}
              />
            </div>
          </div>

          {/* Right Side - Filters Sidebar (1/3) - Hidden on small screens */}
          <div className="hidden lg:block">
            <FactorFilters
              key={`${selectedUniverse}-${selectedQuickFilterId || "none"}`}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onReset={handleFiltersReset}
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
            />
          </div>
        </div>
      </div>

      {/* Filters Modal - Visible only on small screens */}
      <Modal
        show={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        size="lg"
      >
        <div className="p-4 overflow-y-auto max-h-[90vh]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t("factorMimicking.filterIndicators", "Filter Indicators")}
            </h3>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setIsFilterModalOpen(false)}
            >
              <span className="sr-only">{t("factorMimicking.close", "Close")}</span>
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <FactorFilters
            key={`${selectedUniverse}-${selectedQuickFilterId || "none"}`}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleFiltersReset}
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
          />
        </div>
      </Modal>
    </div>
  );
};

export default FactorMimicking;
